// Combat - attack handling, hit detection, damage calculations, and projectiles

import { Vec2, CONST, circlesOverlap, clamp } from './utils.js';
import {
  PIXEL_SCALE, fxClock, quantiseDir, snapToCell,
  GLOW_SMALL, GLOW_FIRE, GLOW_STICKY, ARROW_PALETTE, arrowSprite, stepLine,
  discSprite, ringSprite, layeredDiscSprite,
} from './pixel-art.js';

// Projectile entity (arrows from archer guards). `opts.fire` upgrades
// the arrow to a flaming variant: applies a burn DoT on hit (see
// CONST.FIRE_ARROW_*) and renders with an orange glow + ember trail.
// Drawn as pixel art (pixel-art.js) — the same sprite an archer shows on
// the bow before the shot.
export class Projectile {
  constructor(x, y, dirX, dirY, damage, opts = {}) {
    this.pos = new Vec2(x, y);
    this.dir = new Vec2(dirX, dirY).normalize();
    this.vel = this.dir.mul(CONST.PROJECTILE_SPEED);
    this.damage = damage;
    this.radius = CONST.PROJECTILE_RADIUS;
    this.alive = true;
    this.lifetime = CONST.PROJECTILE_LIFETIME;
    this.fire = !!opts.fire;
    // Sticky arrows glue the player down (no dash, reduced speed) instead
    // of burning them. Mutually exclusive with `fire` at the spawn site.
    this.sticky = !!opts.sticky;
  }

  update(dt, playerPos, playerRadius) {
    if (!this.alive) return false;

    this.pos = this.pos.add(this.vel.mul(dt));
    this.lifetime -= dt;

    if (this.lifetime <= 0) {
      this.alive = false;
      return false;
    }

    // Check hit against player
    if (circlesOverlap(this.pos.x, this.pos.y, this.radius,
                        playerPos.x, playerPos.y, playerRadius)) {
      this.alive = false;
      return true; // hit player
    }

    return false;
  }

  render(renderer) {
    if (!this.alive) return;
    const dir = quantiseDir(this.dir.x, this.dir.y);
    const variant = this.fire ? 'fire' : this.sticky ? 'sticky' : 'plain';
    // The sprite is centred on the head cell; the head is the hit point.
    const x = snapToCell(this.pos.x);
    const y = snapToCell(this.pos.y);
    if (this.fire) {
      // Flickering glow on the head and embers trailing the fletching,
      // fading a cell at a time.
      const time = fxClock();
      renderer.setAlpha(0.3 + 0.1 * Math.sin(time * 23));
      renderer.pixelSprite(x, y, GLOW_SMALL, GLOW_FIRE, PIXEL_SCALE);
      renderer.resetAlpha();
      // Embers continue the shaft's stepped line past the fletching.
      const embers = stepLine(0, 0, -dir.fx, -dir.fy, 7.8, 10.8);
      const [px, py] = Math.abs(dir.fx) > Math.abs(dir.fy) ? [0, 1] : [1, 0];
      const jitter = Math.floor(time * 12) % 2;
      for (let k = 1; k <= embers.length; k++) {
        const [c, r] = embers[k - 1];
        // Embers wander a cell off the line on alternate frames.
        const off = (k + jitter) % 2 ? 0 : 1;
        const ex = x + (c + px * off) * PIXEL_SCALE;
        const ey = y + (r + py * off) * PIXEL_SCALE;
        renderer.setAlpha(1 - k * 0.28);
        renderer.rect(ex - PIXEL_SCALE / 2, ey - PIXEL_SCALE / 2, PIXEL_SCALE, PIXEL_SCALE,
          k === 1 ? '#ff9a3a' : '#cc3a14');
      }
      renderer.resetAlpha();
    } else if (this.sticky) {
      renderer.setAlpha(0.3);
      renderer.pixelSprite(x, y, GLOW_SMALL, GLOW_STICKY, PIXEL_SCALE);
      renderer.resetAlpha();
    }
    renderer.pixelSprite(x, y, arrowSprite(dir, variant), ARROW_PALETTE, PIXEL_SCALE);
  }
}

// Bomb entity (thrown by bombardier guards).
//
// The landing spot is fixed the moment it's thrown and the marker is
// visible for the whole fuse — the blast is a *field*, not a hit, so
// unlike arrows it bypasses i-frames (exactly as the fire-arrow burn
// already does). That's deliberate: it's the one threat a dash cannot
// answer, and the counterplay is to walk out of the circle. Everything
// about it is telegraphed for the full BOMB_FUSE_SEC so it never reads
// as an unfair hit.
// Bomb pixel art: a round charge with a highlight and a fuse; the spark sits
// on the fuse's end (cell offset FUSE_TIP from the centre).
const BOMB_PALETTE = {
  'B': '#2a2a2a', // casing
  'h': '#6a6a6a', // highlight
  'F': '#b08a50', // fuse
};
const BOMB_SPRITE = [
  '       ',
  '     F ',
  '  BBF  ',
  ' BhBBB ',
  ' BBBBB ',
  ' BBBBB ',
  '  BBB  ',
];
const FUSE_TIP = [3, -3];
const SPARK_PALETTE = { 'Y': '#ffe070', 'O': '#ffb347' };
const SPARK_BIG = [
  ' Y ',
  'YYY',
  ' Y ',
];
const SPARK_SMALL = ['O'];
const BLAST_FILL = { 'o': '#ff5522' };
const BLAST_RING = { 'o': '#ff7a2a' };

export class Bomb {
  constructor(x, y, targetX, targetY, damage, opts = {}) {
    this.origin = new Vec2(x, y);
    this.pos = new Vec2(targetX, targetY);
    this.damage = damage;
    this.radius = opts.radius || CONST.BOMB_RADIUS;
    this.fuse = opts.fuse || CONST.BOMB_FUSE_SEC;
    this.timer = this.fuse;
    this.alive = true;
    this.exploded = false;
  }

  // Returns true on the single tick the bomb detonates.
  update(dt) {
    if (!this.alive) return false;
    this.timer -= dt;
    if (this.timer <= 0) {
      this.timer = 0;
      this.alive = false;
      this.exploded = true;
      return true;
    }
    return false;
  }

  // 0 at throw, 1 at detonation. Drives the incoming arc and the warning
  // ring fill.
  get progress() {
    return clamp(1 - this.timer / this.fuse, 0, 1);
  }

  hits(playerPos, playerRadius) {
    const dx = playerPos.x - this.pos.x;
    const dy = playerPos.y - this.pos.y;
    const reach = this.radius + playerRadius;
    return dx * dx + dy * dy < reach * reach;
  }

  render(renderer) {
    if (!this.alive) return;
    const p = this.progress;

    // Ground marker: a pixel ring outlining the whole blast from the first
    // frame, filled toward detonation (a cell of radius at a time) so the
    // urgency is readable without reading a timer.
    const cells = Math.round(this.radius / PIXEL_SCALE);
    const mx = snapToCell(this.pos.x);
    const my = snapToCell(this.pos.y);
    const fill = Math.floor(cells * p);
    if (fill > 0) {
      renderer.setAlpha(0.16 + 0.24 * p);
      renderer.pixelSprite(mx, my, discSprite(fill), BLAST_FILL, PIXEL_SCALE);
      renderer.resetAlpha();
    }
    renderer.setAlpha(0.55);
    renderer.pixelSprite(mx, my, ringSprite(cells), BLAST_RING, PIXEL_SCALE);
    renderer.resetAlpha();

    // The charge itself arcs in over the first 60% of the fuse and then
    // sits on the marker, fuse sparking.
    const flight = Math.min(1, p / 0.6);
    const bx = this.origin.x + (this.pos.x - this.origin.x) * flight;
    const byFlat = this.origin.y + (this.pos.y - this.origin.y) * flight;
    // Parabolic hop, peaking mid-flight.
    const by = byFlat - Math.sin(flight * Math.PI) * 28;
    const cx = snapToCell(bx);
    const cy = snapToCell(by);
    renderer.pixelSprite(cx, cy, BOMB_SPRITE, BOMB_PALETTE, PIXEL_SCALE);
    // Fuse spark blinks faster as the timer runs down.
    const blink = Math.floor(p * 22) % 2 === 0;
    renderer.pixelSprite(
      cx + FUSE_TIP[0] * PIXEL_SCALE, cy + FUSE_TIP[1] * PIXEL_SCALE,
      blink ? SPARK_BIG : SPARK_SMALL, SPARK_PALETTE, PIXEL_SCALE
    );
  }
}

// Calculate damage dealt by attacker to target
// Returns effective damage after armor reduction
export function calcDamage(baseDamage, armor = 0) {
  // Armor reduces damage: effective = base * (100 / (100 + armor))
  const reduction = 100 / (100 + armor);
  return Math.max(1, Math.round(baseDamage * reduction));
}

// Find all entities within attack range of the attacker
// attacker needs: pos, facing, attackRange
// targets is an array of entities with: pos, radius, alive
// Returns array of hit targets
export function findAttackTargets(attacker, targets, attackRange) {
  const hits = [];
  const attackReach = attackRange + attacker.radius;
  const fullArc = !!attacker.fullArcAttack;

  for (const target of targets) {
    if (!target.alive) continue;

    const dist = attacker.pos.dist(target.pos);
    if (dist < attackReach + target.radius) {
      if (fullArc) {
        hits.push(target);
        continue;
      }
      // Check that target is roughly in front of the attacker (180 degree arc)
      const toTarget = target.pos.sub(attacker.pos);
      const dot = toTarget.normalize().dot(attacker.facing);
      // Allow hits in a wide arc in front (dot > -0.3 means roughly 110 degrees each side)
      if (dot > -0.3 || dist < attacker.radius + target.radius) {
        hits.push(target);
      }
    }
  }

  return hits;
}

// Perform a player attack: find targets, apply damage, return hit results
// Returns array of { target, damage } objects.
//
// v1.3.0 card hooks applied here:
//   - player.armorBreak  → calcDamage ignores armor on guards
//   - player.critChance  → per-hit roll for ×2 damage (rolled once per
//     swing, applied to primary + cleave so the result reads as a single
//     "you crit this swing" event rather than partial crits)
//   - player.cleavePct   → after primary hit, every other guard in the
//     attack arc takes cleavePct × primary damage
//   - player.fortifyHealPerKill → heal player when a guard dies from
//     this swing (checked per-target after takeDamage; fires once per
//     kill so a second-strike that finishes a wounded guard still heals)
// `rng` is optional — the live game leaves it null and falls back to
// Math.random for the crit roll. The headless simulator passes its
// seeded rng so the same seed produces the same crit sequence across
// runs (required for byte-identical session dumps and combo scans).
export function performAttack(player, guards, caravans, rng = null) {
  const results = [];
  const rand = rng ? rng.next : Math.random;

  // Per-swing crit roll. One roll covers all targets hit this swing so
  // cleave damage and the primary read as a single crit event.
  const isCrit = player.critChance > 0 && rand() < player.critChance;
  const critMul = isCrit ? 2 : 1;
  // RAGE buff (looted Боевой клич) multiplies all damage this swing.
  const buffMul = player.damageMul || 1;
  const armorFor = (guard) => (player.armorBreak ? 0 : (guard.armor || 0));

  // Find guards in range. First hit = primary (full damage); the rest
  // take cleave damage if the player has the cleave card.
  // Fortify heal accumulates across the swing — multiple kills in one
  // cleave trigger one combined heal that the caller can read via the
  // returned `fortifyHealTotal` (added to the array as a non-iterable
  // property to keep the existing for-of consumers untouched).
  let fortifyHealTotal = 0;
  const applyFortify = (guard) => {
    if (!guard.alive && player.fortifyHealPerKill > 0 && player.alive) {
      fortifyHealTotal += player.heal(player.fortifyHealPerKill, 'fortify');
    }
  };
  const hitGuards = findAttackTargets(player, guards, player.attackRange);
  for (let i = 0; i < hitGuards.length; i++) {
    const guard = hitGuards[i];
    const baseDmg = i === 0 ? player.damage : Math.floor(player.damage * (player.cleavePct || 0));
    if (baseDmg <= 0) continue;
    const dmg = calcDamage(baseDmg * critMul * buffMul, armorFor(guard));
    guard.takeDamage(dmg);
    applyFortify(guard);
    results.push({ target: guard, damage: dmg, type: 'guard', crit: isCrit });
  }
  if (fortifyHealTotal > 0) {
    Object.defineProperty(results, 'fortifyHealTotal', {
      value: fortifyHealTotal,
      enumerable: false,
    });
  }

  // Find caravans in range (can attack caravans directly too)
  const hitCaravans = findAttackTargets(player, caravans, player.attackRange);
  for (const caravan of hitCaravans) {
    const dmg = calcDamage(player.damage * critMul * buffMul);
    caravan.takeDamage(dmg);
    results.push({ target: caravan, damage: dmg, type: 'caravan' });
  }

  return results;
}

// Super strike: a ring blast around the player. Every guard and caravan
// within CONST.SUPER_RADIUS (edge distance) takes SUPER_DAMAGE_MUL × the
// current hit — damage card, rage buff, crit roll and armorBreak all apply;
// cleave, lifesteal and fortify don't (they're per-swing card effects).
// Guards are shoved straight away from the player; caravans (bosses
// included) don't move. Same result shape as performAttack, so callers
// reuse their hit handling. `rng` as in performAttack: one crit roll, and
// only when the player has crit.
export function performSuper(player, guards, caravans, rng = null) {
  const results = [];
  const rand = rng ? rng.next : Math.random;
  const isCrit = player.critChance > 0 && rand() < player.critChance;
  const base = player.damage * CONST.SUPER_DAMAGE_MUL * (isCrit ? 2 : 1) * (player.damageMul || 1);
  const inReach = (target) => player.pos.dist(target.pos) <= CONST.SUPER_RADIUS + (target.radius || 0);

  for (const guard of guards) {
    if (!guard.alive || !inReach(guard)) continue;
    const dmg = calcDamage(base, player.armorBreak ? 0 : (guard.armor || 0));
    guard.takeDamage(dmg);
    if (guard.alive) {
      guard.applyKnockback(guard.pos.x - player.pos.x, guard.pos.y - player.pos.y, CONST.SUPER_KNOCKBACK);
    }
    results.push({ target: guard, damage: dmg, type: 'guard', crit: isCrit });
  }
  for (const caravan of caravans) {
    if (!caravan.alive || !inReach(caravan)) continue;
    const dmg = calcDamage(base);
    caravan.takeDamage(dmg);
    results.push({ target: caravan, damage: dmg, type: 'caravan' });
  }
  return results;
}

// Bomb explosion — a purely visual effect spawned by game.js when a bomb
// detonates on screen. Damage is resolved by Bomb itself on the same tick;
// nothing here feeds back into the game, and its randomness comes from
// Math.random, never a game RNG, so seeded runs are unaffected (the headless
// simulator never creates one).
//
// Timeline (seconds after the blast):
//   0.00–0.10  white-hot flash
//   0.03–0.55  fireball: swells, cools from white to red, shrinks
//   0.00–0.40  shockwave ring racing out to exactly the blast radius —
//              shows the player where the blast reached
//   0.00–0.75  sparks and casing fragments flung outward
//   0.15–1.20  smoke puffs drifting out and up
//   0.00–1.40  scorch mark on the ground, fading after 0.7
const EXPLOSION_LIFE = 1.4;
const FIREBALL_LAYERS = [[0.4, 'c'], [0.7, 'y'], [1, 'o']];
const FIREBALL_HOT = { 'c': '#ffffff', 'y': '#ffe070', 'o': '#ffb347' };
const FIREBALL_WARM = { 'c': '#ffe070', 'y': '#ffb347', 'o': '#ff7a2a' };
const FIREBALL_COOL = { 'c': '#ffb347', 'y': '#ff5a1a', 'o': '#cc3a14' };
const FLASH_LAYERS = [[0.6, 'c'], [1, 'y']];
const SHOCKWAVE = { 'o': '#ffffff' };
const SHOCKWAVE_INNER = { 'o': '#ff7a2a' };
const SCORCH_LAYERS = [[0.5, 'c'], [1, 'o']];
const SCORCH = { 'c': '#2a1e14', 'o': '#4a3624' };
const SMOKE_LAYERS = [[0.5, 'c'], [1, 'o']];
const SMOKE = { 'c': '#5a5450', 'o': '#3e3a38' };
const SPARK_HOT = '#ffe070';
const SPARK_WARM = '#ff7a2a';
const FRAGMENT = '#2a2a2a';

export class Explosion {
  constructor(x, y, radius = CONST.BOMB_RADIUS) {
    this.pos = new Vec2(x, y);
    this.radius = radius;
    this.elapsed = 0;
    this.alive = true;
    this.sparks = [];
    for (let i = 0; i < 24; i++) {
      const a = Math.random() * Math.PI * 2;
      this.sparks.push({
        dx: Math.cos(a),
        dy: Math.sin(a),
        speed: 140 + Math.random() * 180,
        life: 0.35 + Math.random() * 0.4,
        fragment: i % 4 === 0,
        big: i % 3 === 0, // a 2×2-cell chunk rather than a single cell
      });
    }
    this.puffs = [];
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + Math.random() * 0.6;
      this.puffs.push({
        dx: Math.cos(a),
        dy: Math.sin(a),
        dist: radius * (0.15 + Math.random() * 0.3),
        speed: 10 + Math.random() * 18,
        size: 2 + Math.floor(Math.random() * 2),
      });
    }
  }

  update(dt) {
    this.elapsed += dt;
    if (this.elapsed >= EXPLOSION_LIFE) this.alive = false;
  }

  // Ground layer, drawn under entities.
  renderGround(renderer) {
    if (!this.alive) return;
    const e = this.elapsed;
    const alpha = e < 0.7 ? 0.45 : 0.45 * (1 - (e - 0.7) / (EXPLOSION_LIFE - 0.7));
    if (alpha <= 0) return;
    const r = Math.max(2, Math.round(this.radius * 0.45 / PIXEL_SCALE));
    renderer.setAlpha(alpha);
    renderer.pixelSprite(snapToCell(this.pos.x), snapToCell(this.pos.y),
      layeredDiscSprite(r, SCORCH_LAYERS, 'scorch'), SCORCH, PIXEL_SCALE);
    renderer.resetAlpha();
  }

  render(renderer) {
    if (!this.alive) return;
    const e = this.elapsed;
    const cx = snapToCell(this.pos.x);
    const cy = snapToCell(this.pos.y);
    const R = Math.round(this.radius / PIXEL_SCALE);

    // Smoke first, so fire and sparks burn through it.
    if (e > 0.15 && e < 1.2) {
      const k = (e - 0.15) / 1.05;
      renderer.setAlpha(0.7 * (1 - k));
      for (const p of this.puffs) {
        const d = p.dist + p.speed * e * 2;
        const r = p.size + Math.floor(k * 3);
        renderer.pixelSprite(
          snapToCell(this.pos.x + p.dx * d),
          snapToCell(this.pos.y + p.dy * d - 14 * e),
          layeredDiscSprite(r, SMOKE_LAYERS, 'smoke'), SMOKE, PIXEL_SCALE
        );
      }
      renderer.resetAlpha();
    }

    // Shockwave: a white front with an orange cell behind it, easing out to
    // the blast radius.
    if (e < 0.4) {
      const k = e / 0.4;
      const r = Math.max(2, Math.round(R * (1 - (1 - k) * (1 - k))));
      renderer.setAlpha(0.25 + 0.7 * (1 - k));
      renderer.pixelSprite(cx, cy, ringSprite(r), SHOCKWAVE, PIXEL_SCALE);
      renderer.pixelSprite(cx, cy, ringSprite(r - 1), SHOCKWAVE_INNER, PIXEL_SCALE);
      renderer.resetAlpha();
    }

    // Fireball: swells over 0.15 s, holds, then shrinks while cooling.
    if (e > 0.03 && e < 0.55) {
      const grow = Math.min(1, (e - 0.03) / 0.12);
      const shrink = e > 0.3 ? (e - 0.3) / 0.25 : 0;
      const frac = (0.3 + 0.25 * grow) * (1 - 0.6 * shrink);
      const r = Math.max(2, Math.round(R * frac));
      const palette = e < 0.15 ? FIREBALL_HOT : e < 0.3 ? FIREBALL_WARM : FIREBALL_COOL;
      renderer.setAlpha(e > 0.45 ? 1 - (e - 0.45) / 0.1 : 1);
      renderer.pixelSprite(cx, cy, layeredDiscSprite(r, FIREBALL_LAYERS, 'fireball'), palette, PIXEL_SCALE);
      renderer.resetAlpha();
    }

    // Sparks and casing fragments, decelerating. Hot sparks leave a
    // two-cell trail along their flight.
    const S = PIXEL_SCALE;
    for (const s of this.sparks) {
      if (e >= s.life) continue;
      const travel = (s.speed * (1 - Math.exp(-3 * e))) / 3;
      const k = e / s.life;
      const color = s.fragment ? FRAGMENT : k < 0.5 ? SPARK_HOT : SPARK_WARM;
      if (!s.fragment && k < 0.7) {
        for (const [back, a] of [[2 * S, 0.6], [4 * S, 0.3]]) {
          renderer.setAlpha(a * (1 - k));
          renderer.rect(
            snapToCell(this.pos.x + s.dx * (travel - back)) - S / 2,
            snapToCell(this.pos.y + s.dy * (travel - back)) - S / 2,
            S, S, SPARK_WARM
          );
        }
      }
      const size = s.big ? 2 * S : S;
      renderer.setAlpha(1 - k * k);
      renderer.rect(
        snapToCell(this.pos.x + s.dx * travel) - S / 2,
        snapToCell(this.pos.y + s.dy * travel) - S / 2,
        size, size, color
      );
    }
    renderer.resetAlpha();

    // Flash last: the first frames should read as pure white heat.
    if (e < 0.1) {
      const r = Math.max(2, Math.round(R * 0.5));
      renderer.setAlpha(e < 0.05 ? 1 : 1 - (e - 0.05) / 0.05);
      renderer.pixelSprite(cx, cy, layeredDiscSprite(r, FLASH_LAYERS, 'flash'), FIREBALL_HOT, PIXEL_SCALE);
      renderer.resetAlpha();
    }
  }
}
